import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'answer', 'progress', 'progressBar',
    'resultsContainer', 'placeholder',
    'chronotypeIcon', 'chronotypeName', 'chronotypeSubtitle', 'description',
    'wakeTime', 'peakFocus', 'exerciseTime', 'windDown', 'bedTime',
    'tips'
  ]

  // Chronotype definitions with schedules and tips
  chronotypes = {
    lion: {
      name: 'LION',
      subtitle: 'The Early Bird',
      icon: '🦁',
      description: 'You are a Lion chronotype - a natural early riser who thrives in the morning hours. ' +
        'Lions typically wake before dawn feeling energized and accomplish their most important work before noon. ' +
        'You likely have an optimistic personality, are driven to achieve, and prioritize health and fitness. ' +
        'Your energy naturally wanes in the evening, making you ready for bed earlier than most.',
      schedule: {
        wake: '5:30 - 6:00 AM',
        peakFocus: '8:00 AM - 12:00 PM',
        exercise: '5:30 - 6:30 AM',
        windDown: '8:00 PM',
        bed: '9:00 - 10:00 PM'
      },
      tips: [
        'Schedule your most important tasks for morning hours when your cognitive abilities peak',
        'Avoid scheduling critical meetings or creative work in the late afternoon',
        'Front-load your calories - eat a substantial breakfast and lighter dinner',
        'Exercise first thing in the morning for optimal performance',
        'Be mindful at social events - your energy will fade while others are just getting started',
        'Protect your evening wind-down time to maintain your natural rhythm'
      ]
    },
    bear: {
      name: 'BEAR',
      subtitle: 'The Typical Sleeper',
      icon: '🐻',
      description: 'You are a Bear chronotype - the most common type that follows the solar cycle. ' +
        'Bears wake with the sun and feel tired after dark. You experience peak productivity in the mid-morning ' +
        'hours and typically have an afternoon energy dip. Bears are generally good sleepers, friendly, and ' +
        'easy-going. You can adapt to most schedules but perform best with consistency.',
      schedule: {
        wake: '7:00 - 7:30 AM',
        peakFocus: '10:00 AM - 2:00 PM',
        exercise: '7:00 - 8:00 AM or 12:00 - 1:00 PM',
        windDown: '9:30 PM',
        bed: '10:30 - 11:00 PM'
      },
      tips: [
        'Schedule demanding cognitive work for mid-morning when you are most alert',
        'Plan for an afternoon dip - use this time for routine tasks or a brief walk',
        'Avoid hitting snooze - it fragments your sleep and leaves you groggier',
        'Get morning sunlight exposure to reinforce your natural rhythm',
        'Consider a midday workout to combat the afternoon energy slump',
        'Maintain consistent sleep and wake times, even on weekends'
      ]
    },
    wolf: {
      name: 'WOLF',
      subtitle: 'The Night Owl',
      icon: '🐺',
      description: 'You are a Wolf chronotype - a natural night owl who comes alive when others are winding down. ' +
        'Wolves struggle with early mornings and do not hit their stride until noon or later. You are likely ' +
        'creative, impulsive, and introverted. Your most productive hours are in the late afternoon and evening ' +
        'when your creative energy peaks.',
      schedule: {
        wake: '7:30 - 8:30 AM',
        peakFocus: '5:00 - 9:00 PM',
        exercise: '5:00 - 6:00 PM',
        windDown: '11:00 PM',
        bed: '12:00 - 1:00 AM'
      },
      tips: [
        'Delay important decisions and creative work until afternoon when possible',
        'Use mornings for routine, low-stakes tasks that do not require peak cognition',
        'Exercise in the late afternoon or early evening for best performance',
        'Limit blue light exposure after 10 PM despite feeling alert',
        'If you must function early, gradually shift your schedule with light therapy',
        'Eat your largest meal at dinner when your metabolism is most active'
      ]
    },
    dolphin: {
      name: 'DOLPHIN',
      subtitle: 'The Light Sleeper',
      icon: '🐬',
      description: 'You are a Dolphin chronotype - a light sleeper who often struggles with insomnia. ' +
        'Dolphins are highly intelligent, detail-oriented, and tend to be perfectionists with an anxious ' +
        'disposition. You may not have a consistent peak time, instead experiencing productivity in sporadic ' +
        'bursts. Your mind races, making it difficult to fall and stay asleep.',
      schedule: {
        wake: '6:30 - 7:00 AM',
        peakFocus: '10:00 AM - 12:00 PM (varies)',
        exercise: '7:00 - 8:00 AM',
        windDown: '9:00 PM',
        bed: '11:00 - 11:30 PM'
      },
      tips: [
        'Create a strict sleep routine - your body needs consistent signals to wind down',
        'Avoid caffeine after noon as you may be especially sensitive',
        'Exercise in the morning to reduce anxiety and promote better sleep',
        'Practice relaxation techniques like meditation before bed',
        'Keep your bedroom cool, dark, and reserved only for sleep',
        'Consider cognitive behavioral therapy for insomnia (CBT-I) if sleep issues persist',
        'Break large tasks into smaller chunks to manage perfectionist tendencies'
      ]
    }
  }

  connect() {
    this.updateProgress()
  }

  calculate() {
    const answered = this.getAnsweredQuestions()
    this.updateProgress()

    if (answered.length === 7) {
      this.showResults()
    }
  }

  getAnsweredQuestions() {
    const questions = {}
    this.answerTargets.forEach((input) => {
      if (input.checked) {
        questions[input.name] = parseInt(input.value, 10)
      }
    })
    return Object.keys(questions).map((key) => questions[key])
  }

  updateProgress() {
    const answered = this.getAnsweredQuestions()
    const count = answered.length
    const percentage = (count / 7) * 100

    this.progressTarget.textContent = `${count}/7`
    this.progressBarTarget.style.width = `${percentage}%`
  }

  showResults() {
    const answers = this.getAnsweredQuestions()
    const chronotype = this.determineChronotype(answers)
    const data = this.chronotypes[chronotype]

    // Update UI
    this.chronotypeIconTarget.textContent = data.icon
    this.chronotypeNameTarget.textContent = data.name
    this.chronotypeSubtitleTarget.textContent = data.subtitle
    this.descriptionTarget.textContent = data.description

    // Schedule
    this.wakeTimeTarget.textContent = data.schedule.wake
    this.peakFocusTarget.textContent = data.schedule.peakFocus
    this.exerciseTimeTarget.textContent = data.schedule.exercise
    this.windDownTarget.textContent = data.schedule.windDown
    this.bedTimeTarget.textContent = data.schedule.bed

    // Tips
    this.tipsTarget.innerHTML = data.tips
      .map((tip) => `<li class="flex items-start gap-2"><span class="text-gray-400">></span><span>${tip}</span></li>`)
      .join('')

    // Show results, hide placeholder
    this.placeholderTarget.classList.add('hidden')
    this.resultsContainerTarget.classList.remove('hidden')

    // Scroll to results on mobile
    if (window.innerWidth < 1024) {
      this.resultsContainerTarget.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  determineChronotype(answers) {
    // Calculate base score from timing-related questions (Q1-Q5, Q7)
    // Questions measure: wake preference, productivity time, energy pattern, scheduling preference, bedtime, exercise preference
    const timingQuestions = [answers[0], answers[1], answers[3], answers[4], answers[6]]
    const timingScore = timingQuestions.reduce((sum, val) => sum + val, 0)
    const maxTimingScore = 20 // 5 questions * max 4 points

    // Q3 (energy pattern) has special handling for Dolphin detection (value 0)
    const energyPattern = answers[2]

    // Q6 (sleep quality) is key for Dolphin detection
    const sleepQuality = answers[5]

    // Dolphin detection: light/poor sleep quality combined with unpredictable energy
    if (sleepQuality <= 1 || energyPattern === 0) {
      // Strong indicators of Dolphin chronotype
      if (sleepQuality === 0 || (sleepQuality === 1 && energyPattern === 0)) {
        return 'dolphin'
      }
    }

    // Calculate chronotype based on timing score
    const normalizedScore = timingScore / maxTimingScore

    if (normalizedScore >= 0.75) {
      return 'lion'
    } if (normalizedScore >= 0.45) {
      return 'bear'
    }
    return 'wolf'
  }
}
